import React, { useState, useEffect } from 'react';
import CodeEditor from '../components/CodeEditor';
import HtmlPreview from '../components/HtmlPreview';
import RepositorySelector from '../components/RepositorySelector';
import { useGithub } from '../hooks/useGithub';
import { commitFile } from '../services/github';
import { FileContent, Repository } from '../types';

const EditorPage: React.FC = () => {
  const { auth, selectedRepo } = useGithub();
  const [fileContent, setFileContent] = useState<FileContent>({
    content: '',
    filename: '',
    isHtml: false,
  });
  const [commitStatus, setCommitStatus] = useState<{
    loading: boolean;
    success: boolean;
    error: string | null;
  }>({
    loading: false,
    success: false,
    error: null,
  });
  
  // Reset commit status when content changes
  useEffect(() => {
    if (commitStatus.success || commitStatus.error) {
      setCommitStatus({
        loading: false,
        success: false,
        error: null,
      });
    }
  }, [fileContent]);
  
  // Handle code editor changes
  const handleCodeChange = (content: FileContent) => {
    setFileContent(content);
  };
  
  // Handle commit to GitHub
  const handleCommit = async () => {
    if (!auth || !selectedRepo) {
      setCommitStatus({
        loading: false,
        success: false,
        error: 'Please login and select a repository first',
      });
      return;
    }
    
    if (!fileContent.content.trim()) {
      setCommitStatus({
        loading: false,
        success: false,
        error: 'Please enter some content',
      });
      return;
    }
    
    if (!fileContent.filename.trim()) {
      setCommitStatus({
        loading: false,
        success: false,
        error: 'Please enter a filename',
      });
      return;
    }
    
    setCommitStatus({
      loading: true,
      success: false,
      error: null,
    });
    
    try {
      // Commit file to GitHub
      await commitFile({
        repo: selectedRepo.name,
        owner: selectedRepo.owner.login,
        path: fileContent.filename,
        content: fileContent.content,
        message: `Add ${fileContent.filename} via GitHub HTML Tool`,
        token: auth.token,
      });
      
      setCommitStatus({
        loading: false,
        success: true,
        error: null,
      });
    } catch (error) {
      setCommitStatus({
        loading: false,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Code Editor</h1>
        
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-1">
            <RepositorySelector />
            
            {commitStatus.success && (
              <div className="mt-6 p-4 bg-green-50 text-green-800 rounded-md border border-green-200">
                <p className="font-medium">Commit successful!</p>
                <p className="text-sm mt-1">
                  File has been committed to your repository.
                </p>
              </div>
            )}
            
            {commitStatus.error && (
              <div className="mt-6 p-4 bg-red-50 text-red-800 rounded-md border border-red-200">
                <p className="font-medium">Error</p>
                <p className="text-sm mt-1">{commitStatus.error}</p>
              </div>
            )}
            
            <div className="mt-6">
              <button
                onClick={handleCommit}
                disabled={commitStatus.loading || !auth || !selectedRepo}
                className={`w-full py-2 px-4 rounded font-medium ${
                  !auth || !selectedRepo
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'github-btn'
                }`}
              >
                {commitStatus.loading ? 'Committing...' : 'Commit to GitHub'}
              </button>
            </div>
            
            {!auth && (
              <div className="mt-4 text-center text-sm text-gray-600">
                Please login to commit your code
              </div>
            )}
            
            {auth && !selectedRepo && (
              <div className="mt-4 text-center text-sm text-gray-600">
                Please select a repository to commit your code
              </div>
            )}
          </div>
          
          <div className="md:col-span-2">
            <CodeEditor onChange={handleCodeChange} />
            
            {fileContent.isHtml && (
              <div className="mt-8">
                <h2 className="text-xl font-bold mb-4">HTML Preview</h2>
                <HtmlPreview content={fileContent.content} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditorPage; 